import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LockResetIcon from '@mui/icons-material/LockReset';
import { userService } from '../services/user.service';
import { customerService } from '../services/customer.service';
import { useAuth } from '../context/AuthContext';
import { User, UserRole, UserRoleMap, Customer } from '../types';
import { getFunctions, httpsCallable } from 'firebase/functions';

const buildSurface = (theme: Theme) => ({
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
  backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
    theme.palette.primary.dark,
    0.24,
  )})`,
  boxShadow: `0 20px 38px ${alpha(theme.palette.primary.dark, 0.28)}`,
  backdropFilter: 'blur(14px)',
});

const UserManagement: React.FC = () => {
  const theme = useTheme<Theme>();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; userId: string; userName: string }>({
    open: false,
    userId: '',
    userName: '',
  });
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    userId: string;
    userName: string;
    role: UserRole;
    customerId: string;
    customerCode: string;
    companyName: string;
    department: string;
  }>({
    open: false,
    userId: '',
    userName: '',
    role: 'user',
    customerId: '',
    customerCode: '',
    companyName: '',
    department: '',
  });

  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    userId: '',
    userName: '',
    newPassword: '',
    loading: false
  });

  useEffect(() => {
    loadUsers();
    loadCustomers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
      setError('');
    } catch (err) {
      setError('사용자 목록을 불러올 수 없습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await customerService.getAll();
      setCustomers(data);
    } catch (err) {
      console.error('거래처 목록 로딩 실패:', err);
    }
  };

  const handleRoleChange = async (userId: string, currentRole: string) => {
    if (userId === currentUser?.uid) {
      setError('본인의 권한은 변경할 수 없습니다.');
      return;
    }

    try {
      const newRole = currentRole === 'admin' ? 'user' : 'admin';
      await userService.updateUserRole(userId, newRole);
      await loadUsers();
      setError('');
    } catch (err) {
      setError('권한 변경 중 오류가 발생했습니다.');
      console.error(err);
    }
  };

  const handleDeleteClick = (userId: string, userName: string) => {
    if (userId === currentUser?.uid) {
      setError('본인 계정은 삭제할 수 없습니다.');
      return;
    }
    setDeleteDialog({ open: true, userId, userName });
  };

  const handleEditClick = (user: User) => {
    if (user.id === currentUser?.uid) {
      setError('본인 계정은 편집할 수 없습니다.');
      return;
    }
    setEditDialog({
      open: true,
      userId: user.id!,
      userName: user.name,
      role: user.role,
      customerId: user.customerId || '',
      customerCode: user.customerCode || '',
      companyName: user.companyName || '',
      department: user.department || '',
    });
  };

  const handleEditSave = async () => {
    try {
      // validation: customer/partner role일 때 customerId 필수
      if ((editDialog.role === 'customer' || editDialog.role === 'partner') && !editDialog.customerId) {
        setError('거래처를 선택해주세요.');
        return;
      }

      // customerId가 있으면 해당 거래처 정보 가져오기
      let customerCode = editDialog.customerCode;
      let companyName = editDialog.companyName;
      
      if (editDialog.customerId) {
        const selectedCustomer = customers.find(c => c.id === editDialog.customerId);
        if (selectedCustomer) {
          companyName = selectedCustomer.name;
          customerCode = selectedCustomer.id!; // ID를 코드로 사용
        }
      }

      console.log('💾 저장 데이터:', {
        userId: editDialog.userId,
        role: editDialog.role,
        customerId: editDialog.customerId,
        customerCode,
        companyName,
        department: editDialog.department
      });

      await userService.updateUserInfo(editDialog.userId, {
        role: editDialog.role,
        customerId: editDialog.customerId || undefined,
        customerCode: customerCode || undefined,
        companyName: companyName || undefined,
        department: editDialog.department || undefined,
      });
      
      console.log('✅ 저장 완료');
      
      await loadUsers();
      setEditDialog({
        open: false,
        userId: '',
        userName: '',
        role: 'user',
        customerId: '',
        customerCode: '',
        companyName: '',
        department: '',
      });
      setError('');
    } catch (err) {
      console.error('❌ 저장 에러:', err);
      setError('사용자 정보 수정 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await userService.deleteUser(deleteDialog.userId);
      await loadUsers();
      setDeleteDialog({ open: false, userId: '', userName: '' });
      setError('');
    } catch (err) {
      setError('사용자 삭제에 실패했습니다.');
      console.error(err);
    }
  };

  const handlePasswordResetClick = (userId: string, userName: string) => {
    setPasswordDialog({ 
      open: true, 
      userId, 
      userName, 
      newPassword: '', 
      loading: false 
    });
  };

  const handlePasswordReset = async () => {
    if (!passwordDialog.newPassword || passwordDialog.newPassword.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    try {
      setPasswordDialog({ ...passwordDialog, loading: true });
      const functions = getFunctions(undefined, 'asia-northeast3');
      const resetPassword = httpsCallable(functions, 'resetUserPassword');
      
      await resetPassword({
        userId: passwordDialog.userId,
        newPassword: passwordDialog.newPassword
      });

      setPasswordDialog({ open: false, userId: '', userName: '', newPassword: '', loading: false });
      setError('');
      alert(`${passwordDialog.userName}님의 비밀번호가 재설정되었습니다.`);
    } catch (err: any) {
      setPasswordDialog({ ...passwordDialog, loading: false });
      setError(err.message || '비밀번호 재설정 중 오류가 발생했습니다.');
      console.error(err);
    }
  };

  const handlePasswordDialogClose = () => {
    setPasswordDialog({ 
      open: false, 
      userId: '', 
      userName: '', 
      newPassword: '', 
      loading: false 
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          사용자 정보를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
        p: { xs: 2, md: 3 },
      }}
    >
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em', mb: 0.5 }}>
          사용자 관리
        </Typography>
        <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.78) }}>
          시스템에 접근하는 사용자를 관리하고 역할을 부여하세요.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* 모바일: 카드형 레이아웃 */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {users.map((user) => (
            <Card key={user.id} sx={buildSurface(theme)}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ overflow: 'hidden', width: '100%', flex: 1, mr: 1 }}>
                      <Typography variant="h6" sx={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 600,
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        hyphens: 'auto'
                      }}>
                        {user.name}
                      </Typography>
                    </Box>
                    <Chip
                      icon={
                        user.role === 'admin' ? <AdminPanelSettingsIcon /> :
                        user.role === 'customer' || user.role === 'partner' ? <BusinessIcon /> :
                        <PersonIcon />
                      }
                      label={UserRoleMap[user.role as UserRole] || user.role}
                      color={
                        user.role === 'admin' ? 'error' :
                        user.role === 'customer' ? 'primary' :
                        user.role === 'partner' ? 'secondary' :
                        'default'
                      }
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1.5, mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      이메일:
                    </Typography>
                    <Box sx={{ overflow: 'hidden', width: '100%' }}>
                      <Typography variant="body2" sx={{
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        hyphens: 'auto'
                      }}>
                        {user.email}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      부서:
                    </Typography>
                    <Box sx={{ overflow: 'hidden', width: '100%' }}>
                      <Typography variant="body2" sx={{
                        maxWidth: '100%',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        hyphens: 'auto'
                      }}>
                        {user.department || '-'}
                      </Typography>
                    </Box>

                    {(user.role === 'customer' || user.role === 'partner') && user.companyName && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          회사명:
                        </Typography>
                        <Typography variant="body2">
                          {user.companyName}
                        </Typography>
                      </>
                    )}

                    {(user.role === 'customer' || user.role === 'partner') && user.customerCode && (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          코드:
                        </Typography>
                        <Typography variant="body2">
                          {user.customerCode}
                        </Typography>
                      </>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      등록일:
                    </Typography>
                    <Typography variant="body2">
                      {user.createdAt instanceof Date
                        ? user.createdAt.toLocaleDateString('ko-KR')
                        : new Date(user.createdAt).toLocaleDateString('ko-KR')}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditClick(user)}
                    disabled={user.id === currentUser?.uid}
                    sx={{ flex: 1, minWidth: 100 }}
                  >
                    편집
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<LockResetIcon />}
                    onClick={() => handlePasswordResetClick(user.id!, user.name)}
                    disabled={user.id === currentUser?.uid}
                    sx={{ minWidth: 110 }}
                  >
                    비밀번호
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteClick(user.id!, user.name)}
                    disabled={user.id === currentUser?.uid}
                    sx={{ minWidth: 90 }}
                  >
                    삭제
                  </Button>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        /* 데스크톱: 테이블 레이아웃 */
        <Card sx={buildSurface(theme)}>
          <CardContent sx={{ p: 3 }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>이메일</TableCell>
                    <TableCell>이름</TableCell>
                    <TableCell>부서</TableCell>
                    <TableCell>권한</TableCell>
                    <TableCell>회사명</TableCell>
                    <TableCell>등록일</TableCell>
                    <TableCell align="center">작업</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell sx={{ maxWidth: 250 }}>
                        <Box sx={{ overflow: 'hidden', width: '100%' }}>
                          <Typography sx={{
                            maxWidth: '100%',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            hyphens: 'auto'
                          }}>
                            {user.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 150 }}>
                        <Box sx={{ overflow: 'hidden', width: '100%' }}>
                          <Typography sx={{
                            maxWidth: '100%',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            hyphens: 'auto'
                          }}>
                            {user.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 120 }}>
                        <Box sx={{ overflow: 'hidden', width: '100%' }}>
                          <Typography sx={{
                            maxWidth: '100%',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            hyphens: 'auto'
                          }}>
                            {user.department || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            user.role === 'admin' ? <AdminPanelSettingsIcon /> :
                            user.role === 'customer' || user.role === 'partner' ? <BusinessIcon /> :
                            <PersonIcon />
                          }
                          label={UserRoleMap[user.role as UserRole] || user.role}
                          color={
                            user.role === 'admin' ? 'error' :
                            user.role === 'customer' ? 'primary' :
                            user.role === 'partner' ? 'secondary' :
                            'default'
                          }
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 150 }}>
                        <Box sx={{ overflow: 'hidden', width: '100%' }}>
                          <Typography sx={{
                            maxWidth: '100%',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            hyphens: 'auto'
                          }}>
                            {user.companyName || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {user.createdAt instanceof Date
                          ? user.createdAt.toLocaleDateString('ko-KR')
                          : new Date(user.createdAt).toLocaleDateString('ko-KR')}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEditClick(user)}
                          disabled={user.id === currentUser?.uid}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="warning"
                          size="small"
                          onClick={() => handlePasswordResetClick(user.id!, user.name)}
                          disabled={user.id === currentUser?.uid}
                          sx={{ mr: 1 }}
                        >
                          <LockResetIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(user.id!, user.name)}
                          disabled={user.id === currentUser?.uid}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: '', userName: '' })}
        PaperProps={{
          sx: buildSurface(theme),
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>사용자 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{deleteDialog.userName}</strong> 사용자를 삭제하시겠습니까?
          </Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            이 작업은 되돌릴 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, userId: '', userName: '' })}
            variant="outlined"
            color="secondary"
            sx={{
              px: 3,
              borderRadius: 999,
              fontWeight: 600,
              letterSpacing: '0.04em',
              borderColor: alpha(theme.palette.secondary.light, 0.6),
              color: theme.palette.secondary.light,
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ px: 3, borderRadius: 999, fontWeight: 600, letterSpacing: '0.06em' }}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 편집 다이얼로그 */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, userId: '', userName: '', role: 'user', customerId: '', customerCode: '', companyName: '', department: '' })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: buildSurface(theme),
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>사용자 정보 수정</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {editDialog.userName}
            </Typography>

            <FormControl fullWidth>
              <InputLabel>권한</InputLabel>
              <Select
                value={editDialog.role}
                label="권한"
                onChange={(e) => setEditDialog({ ...editDialog, role: e.target.value as UserRole })}
              >
                <MenuItem value="admin">{UserRoleMap.admin}</MenuItem>
                <MenuItem value="user">{UserRoleMap.user}</MenuItem>
                <MenuItem value="customer">{UserRoleMap.customer}</MenuItem>
                <MenuItem value="partner">{UserRoleMap.partner}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="부서"
              value={editDialog.department}
              onChange={(e) => setEditDialog({ ...editDialog, department: e.target.value })}
            />

            {(editDialog.role === 'customer' || editDialog.role === 'partner') && (
              <>
                <FormControl fullWidth required>
                  <InputLabel>거래처 선택</InputLabel>
                  <Select
                    value={editDialog.customerId}
                    label="거래처 선택"
                    onChange={(e) => setEditDialog({ ...editDialog, customerId: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>선택하세요</em>
                    </MenuItem>
                    {customers
                      .filter(c => 
                        editDialog.role === 'customer' ? c.type === 'client' : c.type === 'partner'
                      )
                      .map(customer => (
                        <MenuItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
                {editDialog.customerId && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    선택한 거래처: {customers.find(c => c.id === editDialog.customerId)?.name}
                  </Alert>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setEditDialog({ open: false, userId: '', userName: '', role: 'user', customerId: '', customerCode: '', companyName: '', department: '' })}
            variant="outlined"
            color="secondary"
            sx={{
              px: 3,
              borderRadius: 999,
              fontWeight: 600,
              letterSpacing: '0.04em',
              borderColor: alpha(theme.palette.secondary.light, 0.6),
              color: theme.palette.secondary.light,
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            sx={{ px: 3, borderRadius: 999, fontWeight: 600, letterSpacing: '0.06em' }}
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>

      {/* 비밀번호 재설정 다이얼로그 */}
      <Dialog
        open={passwordDialog.open}
        onClose={handlePasswordDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: buildSurface(theme),
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>비밀번호 재설정</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {passwordDialog.userName}
            </Typography>
            <TextField
              fullWidth
              type="password"
              label="새 비밀번호"
              value={passwordDialog.newPassword}
              onChange={(e) => setPasswordDialog({ ...passwordDialog, newPassword: e.target.value })}
              placeholder="최소 6자 이상"
              disabled={passwordDialog.loading}
              helperText="최소 6자 이상의 비밀번호를 입력하세요"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handlePasswordDialogClose}
            variant="outlined"
            color="secondary"
            disabled={passwordDialog.loading}
            sx={{
              px: 3,
              borderRadius: 999,
              fontWeight: 600,
              letterSpacing: '0.04em',
              borderColor: alpha(theme.palette.secondary.light, 0.6),
              color: theme.palette.secondary.light,
              '&:hover': {
                borderColor: theme.palette.secondary.light,
                backgroundColor: alpha(theme.palette.secondary.light, 0.12),
              },
            }}
          >
            취소
          </Button>
          <Button
            onClick={handlePasswordReset}
            variant="contained"
            color="warning"
            disabled={passwordDialog.loading}
            sx={{ px: 3, borderRadius: 999, fontWeight: 600, letterSpacing: '0.06em' }}
          >
            {passwordDialog.loading ? <CircularProgress size={24} /> : '재설정'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
